import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useDarkMode } from '../../hooks/useDarkMode'
import { seriesConfig } from '../../config/series.generated'
import SeriesIcon from '../ui/SeriesIcon'

export default function MainNav() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isSeriesDropdownOpen, setIsSeriesDropdownOpen] = useState(false)
  const [isTreasuryDropdownOpen, setIsTreasuryDropdownOpen] = useState(false)
  const treasuryPages = [
    { href: '/treasury-risk-learning-infrastructure', label: 'Learning infrastructure' },
    { href: '/trusted-treasury-voices', label: 'Trusted sources' },
    { href: '/vendor-evaluation-checklist', label: 'Vendor checklist' },
    { href: '/problems-solved', label: 'Problems solved' },
  ]
  const { isDark, toggleDarkMode } = useDarkMode()
  const location = useLocation()

  return (
    <nav className="no-print bg-navy-800 dark:bg-navy-900 shadow-lg">
      <div className="max-w-7xl mx-auto container-padding">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center space-x-3"
          >
            {/* Logo Icon */}
            <div className="w-10 h-10 flex items-center justify-center bg-gold-600 rounded-lg">
              <svg className="w-6 h-6 text-navy-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="font-serif text-lg font-bold text-white leading-tight tracking-wide">
                RONDANINI PUBLISHING
              </span>
              <span className="text-xs text-gold-400 leading-tight tracking-wider">
                Professional Financial Education
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link 
              to="/" 
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/' 
                  ? 'text-gold-400' 
                  : 'text-gray-300 hover:text-gold-400'
              }`}
            >
              Home
            </Link>

            {/* Main Site Link */}
            <a 
              href="https://www.rondanini.net" 
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-gray-300 hover:text-gold-400 transition-colors"
            >
              Main Site
            </a>

            {/* Series Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsSeriesDropdownOpen(!isSeriesDropdownOpen)}
                className="text-sm font-medium text-gray-300 hover:text-gold-400 transition-colors flex items-center space-x-1"
              >
                <span>Series</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isSeriesDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-white dark:bg-navy-800 rounded-xl shadow-card border border-gray-200 dark:border-navy-700 py-2 z-50">
                  {seriesConfig.map((series) => (
                    <Link
                      key={series.slug}
                      to={`/series/${series.slug}`}
                      className="flex items-center px-4 py-3 hover:bg-gray-50 dark:hover:bg-navy-700 transition-colors"
                      onClick={() => setIsSeriesDropdownOpen(false)}
                    >
                      <SeriesIcon icon={series.icon} size="md" className="mr-3" />
                      <div>
                        <div className="text-sm font-medium text-navy-800 dark:text-white">
                          {series.title}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {series.manuals.length} manuals
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => setIsTreasuryDropdownOpen(!isTreasuryDropdownOpen)}
                className="text-sm font-medium text-gray-300 hover:text-gold-400 transition-colors flex items-center space-x-1"
                aria-expanded={isTreasuryDropdownOpen}
              >
                <span>Treasury</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isTreasuryDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-navy-800 rounded-xl shadow-card border border-gray-200 dark:border-navy-700 py-2 z-50">
                  {treasuryPages.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className="block px-4 py-3 text-sm font-medium text-navy-800 dark:text-white hover:bg-gray-50 dark:hover:bg-navy-700"
                      onClick={() => setIsTreasuryDropdownOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* CashFX Link */}
            <a
              href="/cashfx"
              className="text-sm font-medium text-gray-300 hover:text-gold-400 transition-colors"
            >
              Cash FX Toolkit
            </a>

            <a
              href="/derivatives-toolkits"
              className="text-sm font-medium text-gray-300 hover:text-gold-400 transition-colors"
            >
              FX Derivatives Toolkits
            </a>

            <Link
              to="/python-test"
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/python-test'
                  ? 'text-gold-400'
                  : 'text-gray-300 hover:text-gold-400'
              }`}
            >
              Python Playground
            </Link>

            {/* My Library Link */}
            <Link
              to="/my-library"
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/my-library'
                  ? 'text-gold-400'
                  : 'text-gray-300 hover:text-gold-400'
              }`}
            >
              My Library
            </Link>

            {/* Login Link */}
            <a
              href="/login.html"
              className="text-sm font-medium text-gray-300 hover:text-gold-400 transition-colors"
            >
              Login
            </a>

            <Link
              to="/subscribe"
              className={`text-sm font-semibold px-4 py-2 rounded-lg transition-colors ${
                location.pathname === '/subscribe'
                  ? 'bg-gold-600 text-navy-800'
                  : 'bg-gold-600 text-navy-800 hover:bg-gold-500'
              }`}
            >
              Get access
            </Link>

            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-lg bg-navy-700 hover:bg-navy-600 transition-colors"
              aria-label="Toggle dark mode"
            >
              {isDark ? (
                <svg className="w-5 h-5 text-gold-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg bg-navy-700 hover:bg-navy-600 transition-colors"
            aria-label="Toggle mobile menu"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-navy-700">
            <div className="space-y-4">
              <Link 
                to="/" 
                className="block text-sm font-medium text-gray-300 hover:text-gold-400 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Home
              </Link>
              
              <a 
                href="https://www.rondanini.net" 
                target="_blank"
                rel="noopener noreferrer"
                className="block text-sm font-medium text-gray-300 hover:text-gold-400 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Main Site
              </a>

              <div className="space-y-2">
                <div className="text-sm font-medium text-white">Treasury</div>
                {treasuryPages.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="block pl-4 py-2 text-sm text-gray-400 hover:text-gold-400 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>

              <a
                href="/cashfx"
                className="block text-sm font-medium text-gray-300 hover:text-gold-400 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Cash FX Toolkit
              </a>

              <a
                href="/derivatives-toolkits"
                className="block text-sm font-medium text-gray-300 hover:text-gold-400 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                FX Derivatives Toolkits
              </a>

              <Link
                to="/python-test"
                className="block text-sm font-medium text-gray-300 hover:text-gold-400 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Python Playground
              </Link>

              <Link
                to="/my-library"
                className="block text-sm font-medium text-gray-300 hover:text-gold-400 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                My Library
              </Link>

              <Link
                to="/subscribe"
                className="block text-sm font-semibold text-center bg-gold-600 text-navy-800 py-3 rounded-lg hover:bg-gold-500 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Get access
              </Link>

              <a
                href="/login.html"
                className="block text-sm font-medium text-gray-300 hover:text-gold-400 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Login
              </a>

              <div className="space-y-2">
                <div className="text-sm font-medium text-white">Series</div>
                {seriesConfig.map((series) => (
                  <Link
                    key={series.slug}
                    to={`/series/${series.slug}`}
                    className="flex items-center pl-4 py-2 text-sm text-gray-400 hover:text-gold-400 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <SeriesIcon icon={series.icon} size="sm" className="mr-3" />
                    {series.title}
                  </Link>
                ))}
              </div>
              
              <button
                onClick={() => {
                  toggleDarkMode()
                  setIsMobileMenuOpen(false)
                }}
                className="flex items-center text-sm font-medium text-gray-300 hover:text-gold-400 transition-colors"
              >
                {isDark ? '☀️' : '🌙'} {isDark ? 'Light Mode' : 'Dark Mode'}
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}
